import { useCallback, useMemo } from "react";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";

/**
 * Manages file navigation (previous/next) and keyboard shortcuts
 */
export const useFileNavigation = (
  file: FormattedFile | null,
  files: FormattedFile[],
  onFileOpen: (file: FormattedFile) => void
) => {
  const currentFileIndex = useMemo(() => {
    if (!file) return -1;
    return files.findIndex((f) => f.id === file.id);
  }, [file, files]);

  const hasPrevious = currentFileIndex > 0;
  const hasNext = currentFileIndex >= 0 && currentFileIndex < files.length - 1;

  const handlePrevious = useCallback(() => {
    if (hasPrevious) {
      onFileOpen(files[currentFileIndex - 1]);
    }
  }, [hasPrevious, currentFileIndex, files, onFileOpen]);

  const handleNext = useCallback(() => {
    if (hasNext) {
      onFileOpen(files[currentFileIndex + 1]);
    }
  }, [hasNext, currentFileIndex, files, onFileOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      }
    },
    [handlePrevious, handleNext]
  );

  return {
    hasPrevious,
    hasNext,
    handlePrevious,
    handleNext,
    handleKeyDown
  };
};
